var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, createRef, useEffect, useRef } from 'react';
import clsx from 'clsx';
import * as Yup from 'yup';
import AddressLookup from '../AddressLookup/AddressLookup';
import Button from '../Button/Button';
import TextField from '../TextField/TextField';
import { FormikContext } from 'formik';
import { addressFields, errorMessages, getCountryFromAlpha2Code, replaceCountryCodes, inputValidationRegex, SiteContext, phoneValidation, trimPhoneSpace, } from '../../utils/common';
import useStyles from '../ProductDetailPurchase/ProductDetailPurchase.styles';
import formatDate from 'date-fns/format';
import DeliveryOptions from '../DeliveryOptions/DeliveryOptions';
import { format } from 'date-fns';
import Link from '../Link/Link';
import DeliveryInstructions from '../DeliveryInstructions/DeliveryInstructions';
import { Box, useTheme } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
export const validationConfig = Yup.object().shape({
    firstName: Yup.string()
        .trim()
        .min(1, errorMessages(1).minChars)
        .max(50, errorMessages(50).maxChars)
        .required('Please enter a name')
        .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    lastName: Yup.string()
        .trim()
        .min(1, errorMessages(1).minChars)
        .max(40, errorMessages(40).maxChars)
        .required('Please enter a last name')
        .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    phone: Yup.string()
        .trim()
        .required(errorMessages().required)
        .max(50, errorMessages(50).maxChars)
        .when('isLoggedIn', {
        is: (isLoggedIn) => !!isLoggedIn,
        then: Yup.string()
            .matches(/^[+]?[0-9 ]*$/, errorMessages().phone)
            .min(4, errorMessages(4).minChars),
        otherwise: Yup.string()
            .test('Phone Number Validation', errorMessages().phone, (value) => phoneValidation(value))
            .min(9, errorMessages(9).minChars),
    }),
    recipientAddress: Yup.string()
        .min(3, errorMessages(4).minChars)
        .required(errorMessages().required)
        .oneOf([Yup.ref('originalAddressSelection'), null], "Please select an address from the list or choose 'enter manually'"),
    addressLine1: Yup.string()
        .required('Please enter address line 1')
        .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    addressLine2: Yup.string().matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    city: Yup.string()
        .required('Please enter a city')
        .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    county: Yup.string().when('isManualAddress', {
        is: true,
        then: Yup.string()
            .required('Please enter a county')
            .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    }),
    postalCode: Yup.string()
        .when('shouldValidatePostCode', {
        is: true,
        then: Yup.string().required('Please enter a postcode'),
    })
        .matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    country: Yup.string().required('Please select a country'),
    isManualAddress: Yup.boolean(),
    deliveryInstructionNote: Yup.string().matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    isLoggedIn: Yup.boolean(),
});
export const initialValues = {
    firstName: '',
    lastName: '',
    recipientAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postalCode: '',
    country: '',
    isManualAddress: false,
    isCalendarPicked: false,
    isFuneralDirector: true,
    shouldValidatePostCode: true,
    deliveryDate: '',
    selectedDeliveryOptionIndex: 0,
    productAvailability: [],
};
const PurchaseTributeDelivery = ({ step, currentStep, setCurrentStep, formatAddressCallback, variant, initialRecipientAddress, selectedServiceDescription, countryCode, isProductFulfillableCallback, handleFuneralSaveManualAddress, setDeliveryOptions, productKey, deliveryOptionsInfo, }) => {
    var _a, _b;
    const { classes } = useStyles();
    const theme = useTheme();
    const { shorthand, getCustomer } = useContext(SiteContext);
    const formikContext = useContext(FormikContext);
    const { setFieldValue } = formikContext;
    const setManualAddressChosen = (isManualAddress) => formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('isManualAddress', isManualAddress);
    const isTabletAndBelow = useMediaQuery({ query: `(max-width: ${theme.breakpoints.values.md}px)` });
    const stepRef = createRef();
    const isLoggedIn = !!((_b = (_a = getCustomer === null || getCustomer === void 0 ? void 0 : getCustomer.data) === null || _a === void 0 ? void 0 : _a.customer) === null || _b === void 0 ? void 0 : _b.id);
    const { funeralServiceDate, funeralServiceTime, deliveryDate, deliveryOptions, deliveryError, selectedDeliveryOptionIndex, addressLine1, addressLine2, city, county, postalCode, country, isFuneralDirector, } = formikContext.values;
    const funeralServiceDateString = funeralServiceDate && formatDate(funeralServiceDate, 'yyyy-MM-dd');
    const handleAddressFormatCall = (suggestion, format) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield formatAddressCallback({
            suggestion,
            format,
            variant,
            countryCode,
            startDate: funeralServiceDateString,
            endDate: funeralServiceDateString,
        });
        if (!response || response.error) {
            throw new Error(`Address formatting returned an error response: ${response === null || response === void 0 ? void 0 : response.error}`);
        }
        else if (!response.data) {
            throw new Error('No results returned from address formatting');
        }
        const { address, availability } = response.data.formattedAddressAndProductAvailability;
        if (!availability.length) {
            setFieldValue('deliveryError', 'There are no available delivery options');
        }
        else {
            setFieldValue('deliveryError', '');
            setDeliveryOptions(availability, formikContext, funeralServiceDateString);
        }
        return address.addressLine1 ? address : null;
    });
    const countryDetails = getCountryFromAlpha2Code(countryCode);
    const formattedAddress = replaceCountryCodes(formikContext.values);
    useEffect(() => {
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('isLoggedIn', isLoggedIn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        const selectedDeliveryOptionData = deliveryOptions[selectedDeliveryOptionIndex];
        const extraParams = productKey ? { productKey, variantKey: variant, ocassion: 'Funeral' } : { variant };
        const response = yield isProductFulfillableCallback(Object.assign(Object.assign({}, extraParams), { deliveryDate, deliveryServiceCode: selectedDeliveryOptionData.serviceCode, address: {
                addressLine1,
                addressLine2,
                city,
                county,
                postalCode,
                country,
            }, funeralServiceTime: (_c = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _c === void 0 ? void 0 : _c.funeralServiceTime, funeralServiceDate: (_d = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _d === void 0 ? void 0 : _d.funeralServiceDate }));
        if (response.error) {
            setFieldValue('deliveryError', 'There was a problem checking the availability, please try again.');
        }
        else if (!response.data || response.data.isProductFulfillable === false) {
            setFieldValue('deliveryError', 'The product is currently unavailable for the location and date.');
        }
        else {
            setFieldValue('deliveryError', '');
            setFieldValue('deliveryOptionalRuleId', selectedDeliveryOptionData.optionalRuleId);
            setFieldValue('deliveryStandardRuleId', selectedDeliveryOptionData.standardRuleId);
            formikContext.submitForm();
        }
    });
    const switchAddressType = (isFuneralDirector) => {
        var _a;
        setFieldValue('isFuneralDirector', isFuneralDirector);
        (_a = stepRef === null || stepRef === void 0 ? void 0 : stepRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
    };
    const relevantLabel = formikContext.values.isFuneralDirector ? 'Funeral Director’s' : 'Recipient’s';
    const saveManualAddressAddress = () => {
        handleFuneralSaveManualAddress({
            startDate: funeralServiceDateString,
            endDate: funeralServiceDateString,
            formikContext,
        });
    };
    const prevStepRef = useRef(currentStep);
    useEffect(() => {
        // Check if the step has changed and matches the current step
        if (currentStep === step && prevStepRef.current !== currentStep) {
            const element = stepRef === null || stepRef === void 0 ? void 0 : stepRef.current;
            if (element) {
                const offset = isTabletAndBelow ? 80 : 40; // Adjust this value based on your header height or offset
                const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - offset;
                // Scroll smoothly to the element
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        }
        // Update the reference for the previous step
        prevStepRef.current = currentStep;
    }, [currentStep, step, stepRef, isTabletAndBelow]);
    return (_jsxs("div", { ref: stepRef, children: [_jsxs(Box, { className: clsx(classes.heading, currentStep < step && classes.pending), children: [step, ". Where should our florist deliver this tribute?"] }), currentStep === step ? (_jsxs(_Fragment, { children: [isFuneralDirector && (_jsx("div", { className: classes.tributeDeliveryMessage, children: "Usually the florist will contact the Funeral Director to arrange the delivery of the tribute." })), _jsxs("div", { className: classes.input, children: [_jsx("p", { className: classes.caption, children: _jsxs("strong", { children: [relevantLabel, " details"] }) }), _jsx(TextField, { name: "firstName", label: `${relevantLabel} first name`, placeholder: "e.g. John", inputProps: { maxLength: 50 }, required: true, fullWidth: true, trimOnBlur: true })] }), _jsx("div", { className: classes.input, children: _jsx(TextField, { name: "lastName", label: `${relevantLabel} last name`, placeholder: "e.g. Smith", inputProps: { maxLength: 40 }, required: true, fullWidth: true, trimOnBlur: true }) }), _jsxs("div", { className: classes.input, children: [_jsx(TextField, { name: "phone", label: `${relevantLabel} phone number`, placeholder: "e.g. 01234 567890", required: true, fullWidth: true, trimOnBlur: true, inputProps: { maxLength: 50 }, onInput: trimPhoneSpace }), _jsx("p", { className: classes.caption, children: "We will only use this to contact them about the delivery of the tribute." })] }), _jsxs("div", { className: classes.input, children: [_jsx("p", { className: classes.caption, children: _jsxs("strong", { children: [relevantLabel, " address"] }) }), isFuneralDirector && (_jsx("p", { className: classes.caption, children: "All tributes should go directly to the Funeral Home so that they are looked after and arrive on the day and time of the service." })), _jsx(AddressLookup, { name: "recipientAddress", label: `Search for ${relevantLabel} address`, placeholder: `Try the ${isFuneralDirector
                                    ? `company name or ${shorthand === 'ie' ? 'eircode' : 'postcode'}`
                                    : `${shorthand === 'ie' ? 'eircode' : 'postcode'} or street name`}`, addressFields: addressFields, provideManualAddress: true, setManualAddressChosen: setManualAddressChosen, handleAddressFormatCall: handleAddressFormatCall, required: true, fullWidth: true, initialValue: initialRecipientAddress, handleSaveManualAddressCallback: saveManualAddressAddress, handleClearAddress: () => setFieldValue('deliveryOptions', []), countryCode: countryDetails === null || countryDetails === void 0 ? void 0 : countryDetails.codeAlpha3, countryList: [countryDetails] })] }), _jsx(Link, { className: classes.link, onClick: () => switchAddressType(!isFuneralDirector), label: isFuneralDirector ? 'Use different delivery address' : 'Use Funeral Director’s delivery address' }), funeralServiceDate && !!deliveryOptions.length && (_jsx("div", { className: classes.input, children: _jsx(DeliveryOptions, { heading: `Your delivery option to arrive in time for ${format(funeralServiceDate, 'EEEE do LLLL')} ${funeralServiceTime}`, deliveryOptions: deliveryOptions, selectedServiceDescription: selectedServiceDescription, deliveryOptionsInfo: deliveryOptionsInfo }) })), !isFuneralDirector && _jsx(DeliveryInstructions, {}), !!deliveryError && _jsx("div", { className: classes.error, children: deliveryError }), _jsx("div", { className: classes.next, children: _jsx(Button, { variant: "primary", disabled: !(deliveryOptions === null || deliveryOptions === void 0 ? void 0 : deliveryOptions.length), title: "Next: What message will you send", thinOnMobile: true, fullWidth: true, onClick: () => handleSubmit() }) })] })) : currentStep > step ? (_jsx("div", { className: classes.completed, children: _jsxs(TextField, { name: "placeholderRecipientAddress", fullWidth: true, completed: true, onClick: () => setCurrentStep(step), children: [_jsx("div", { children: `${formattedAddress.firstName} ${formattedAddress.lastName}` }), _jsx("div", { children: `${formattedAddress.phone}` }), addressFields.map((addressLine, i) => {
                            const addressValue = formattedAddress[addressLine];
                            return !!addressValue && _jsx("div", { children: addressValue }, `${addressLine}${i}`);
                        })] }) })) : null] }));
};
export default PurchaseTributeDelivery;
